import { useClerk, useReverification } from "@clerk/tanstack-react-start";
import type { ClerkAPIError } from "@clerk/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import { getClerkErrorMessage } from "~/lib/clerk-errors";

import { SettingsCardTitle } from "./-components/settings-card-title";

const PasswordSettingsSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type PasswordSettingsForm = z.infer<typeof PasswordSettingsSchema>;

export const Route = createFileRoute(
  "/_user/settings/profile/password-settings",
)({
  staticData: { title: "Password" },
  component: PasswordSettingsPage,
});

function PasswordSettingsPage() {
  const clerk = useClerk();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [needsReverify, setNeedsReverify] = useState(false);

  const { register, handleSubmit, formState, reset } =
    useForm<PasswordSettingsForm>({
      resolver: zodResolver(PasswordSettingsSchema),
      defaultValues: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
    });

  const { errors } = formState;

  const updatePassword = useReverification(
    async (currentPassword: string, newPassword: string) => {
      if (!clerk.user) {
        throw new Error("You must be signed in to update password");
      }
      await clerk.user.updatePassword({ currentPassword, newPassword });
    },
  );

  const onSubmit = async (data: PasswordSettingsForm) => {
    setIsSubmitting(true);
    try {
      const result = await updatePassword(
        data.currentPassword,
        data.newPassword,
      );
      // useReverification returns null if user cancels the verification modal
      if (result === null) {
        return;
      }
      reset();
      toast.success("Password changed");
    } catch (error) {
      const err = error as ClerkAPIError;
      if (err.code === "session_reverification_required") {
        setNeedsReverify(true);
        toast.error("Security verification required", {
          description: "Please confirm your identity to perform this action.",
        });
        return;
      }

      const message = getClerkErrorMessage(error, "Failed to change password");
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReverify = async () => {
    try {
      await clerk.openUserProfile({
        appearance: { elements: { rootBox: "mx-auto" } },
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleRecoveryPassword = async () => {
    try {
      await clerk.signOut();
      await navigate({ to: "/forgot-password" });
    } catch (error) {
      console.error("Failed to sign out:", error);
      toast.error("Failed to initiate password recovery");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {needsReverify && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-purple-200 bg-purple-50 p-4">
            <div className="flex items-center gap-3">
              <span className="flex size-8 items-center justify-center rounded-xl bg-purple-100">
                <ShieldCheck className="size-4 text-purple-700" />
              </span>
              <div className="space-y-1">
                <p className="text-sm font-medium text-purple-900">
                  Verification required
                </p>
                <p className="text-sm text-purple-700">
                  Please confirm your identity to apply changes.
                </p>
              </div>
            </div>
            <Button size="sm" variant="primary" onClick={handleReverify}>
              Verify now
            </Button>
          </div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <SettingsCardTitle icon={KeyRound}>Change password</SettingsCardTitle>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-4">
                <FormField
                  label="Current password"
                  error={errors.currentPassword?.message}
                >
                  <div className="relative">
                    <Input
                      {...register("currentPassword")}
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="Enter current password"
                      className="pr-12"
                      error={!!errors.currentPassword}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                      className="absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-400 transition-colors hover:text-gray-600"
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="size-4" />
                      ) : (
                        <Eye className="size-4" />
                      )}
                    </button>
                  </div>
                </FormField>

                <div className="flex flex-col gap-2">
                  <p className="text-text-tertiary text-sm">Forgot password?</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-fit text-purple-700 hover:text-purple-800"
                    onClick={handleRecoveryPassword}
                  >
                    Recover current password
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <FormField
                  label="New password"
                  error={errors.newPassword?.message}
                >
                  <div className="relative">
                    <Input
                      {...register("newPassword")}
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      className="pr-12"
                      error={!!errors.newPassword}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-400 transition-colors hover:text-gray-600"
                    >
                      {showNewPassword ? (
                        <EyeOff className="size-4" />
                      ) : (
                        <Eye className="size-4" />
                      )}
                    </button>
                  </div>
                </FormField>

                <FormField
                  label="Confirm password"
                  error={errors.confirmPassword?.message}
                >
                  <div className="relative">
                    <Input
                      {...register("confirmPassword")}
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-type new password"
                      className="pr-12"
                      error={!!errors.confirmPassword}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-400 transition-colors hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="size-4" />
                      ) : (
                        <Eye className="size-4" />
                      )}
                    </button>
                  </div>
                </FormField>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="flex justify-end"
            >
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                variant="primary"
                className="gap-1.5"
              >
                Apply new password
              </Button>
            </motion.div>
          </form>
        </Card>
      </motion.div>
    </div>
  );
}
